import Phaser from 'phaser'
import { InventoryItem } from '../types/GameTypes'

const DESPAWN_TIME = 60000 // 60 seconds

export class LootBag extends Phaser.GameObjects.Container {
  private items: InventoryItem[]
  private gold: number

  private sprite: Phaser.GameObjects.Graphics
  private glowSprite: Phaser.GameObjects.Graphics

  private despawnTimer: Phaser.Time.TimerEvent | null = null

  // Callback when bag despawns (not collected)
  public onDespawn: (() => void) | null = null

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    items: InventoryItem[],
    gold: number
  ) {
    super(scene, x, y)

    this.items = items
    this.gold = gold

    scene.add.existing(this)

    // Create glow effect (behind bag)
    this.glowSprite = scene.add.graphics()
    this.drawGlow()
    this.add(this.glowSprite)

    // Create bag sprite
    this.sprite = scene.add.graphics()
    this.drawBag()
    this.add(this.sprite)

    // Set depth for proper layering
    this.setDepth(this.y - 1)

    // Animate entrance
    this.setScale(0)
    scene.tweens.add({
      targets: this,
      scale: 1,
      duration: 200,
      ease: 'Back.easeOut'
    })

    // Pulse animation for glow
    scene.tweens.add({
      targets: this.glowSprite,
      alpha: 0.3,
      duration: 800,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    })

    // Set up despawn timer
    this.despawnTimer = scene.time.delayedCall(DESPAWN_TIME, this.despawn, [], this)
  }

  /**
   * Draw the bag sprite
   */
  private drawBag(): void {
    const g = this.sprite

    // Bag body (brown sack)
    g.fillStyle(0x8b4513)
    g.fillEllipse(0, 2, 16, 12)

    // Bag top (tied part)
    g.fillStyle(0xa0522d)
    g.fillEllipse(0, -4, 10, 6)

    // Bag tie
    g.fillStyle(0x654321)
    g.fillRect(-2, -6, 4, 4)

    // Highlight
    g.fillStyle(0xcd853f, 0.5)
    g.fillEllipse(-3, 0, 6, 4)

    // Gold coin peeking out if there's gold
    if (this.gold > 0) {
      g.fillStyle(0xffd700)
      g.fillCircle(4, 3, 4)
      g.fillStyle(0xffed4a)
      g.fillCircle(3, 2, 2)
    }
  }

  /**
   * Draw the glow effect
   */
  private drawGlow(): void {
    const g = this.glowSprite

    // Determine glow color based on contents
    let color = 0xffd700 // Gold color default

    if (this.items.length > 0) {
      // Green glow for items
      color = 0x22c55e
    }

    g.fillStyle(color, 0.6)
    g.fillCircle(0, 0, 20)
    g.fillStyle(color, 0.3)
    g.fillCircle(0, 0, 28)
  }

  /**
   * Get the contents of the bag
   */
  public getContents(): { items: InventoryItem[]; gold: number } {
    return {
      items: [...this.items],
      gold: this.gold
    }
  }

  /**
   * Handle bag being collected
   */
  public collect(): void {
    // Cancel despawn timer
    if (this.despawnTimer) {
      this.despawnTimer.destroy()
      this.despawnTimer = null
    }

    // Animate collection
    this.scene.tweens.add({
      targets: this,
      scale: 0,
      alpha: 0,
      y: this.y - 20,
      duration: 200,
      ease: 'Power2',
      onComplete: () => {
        this.destroy()
      }
    })
  }

  /**
   * Handle bag despawning (not collected)
   */
  private despawn(): void {
    // Fade out
    this.scene.tweens.add({
      targets: this,
      alpha: 0,
      duration: 1000,
      onComplete: () => {
        if (this.onDespawn) {
          this.onDespawn()
        }
        this.destroy()
      }
    })
  }

  /**
   * Clean up
   */
  public destroy(fromScene?: boolean): void {
    if (this.despawnTimer) {
      this.despawnTimer.destroy()
      this.despawnTimer = null
    }
    super.destroy(fromScene)
  }
}
